import React, { useState } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Button,
    Typography
} from "@mui/material";

import useMicroTipApi from "../../../api/MicroTipApi";
import ScienceIcon from "@mui/icons-material/Science";
import ConfirmDialog from "../../Common/ConfirmDialog";

export default function ({ onCalibrate, setResultDataMap, rows, parameters, setParameters, splines }) {
   
    const [description, setDescription] = useState("");
    console.log("HEADER ROWS:", rows);
    console.log("Parameter ROWS:", parameters);   
    const { callApi, loading, error } = useMicroTipApi(parameters, rows, splines);
    const [openValidationDialog, setOpenValidationDialog] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    // Transform API → History Grid 
    const transformApiToHistory = (data) => {
        if (!data) return null;

        const safeNumber = (value) => {
            if (value === null || value === undefined || value === "") {
                return 0;
            }

            const numberValue = Number(value);

            return Number.isNaN(numberValue) ? 0 : numberValue;
        };

        const formatValue = (value) => {
            // Convert array values to comma-separated single-row values
           if (Array.isArray(value)) {
    const allZero = value.every(
        (item) => safeNumber(item) === 0
    );

    if (allZero) {
        return "";
    }

    return value
        .map((item) => safeNumber(item).toFixed(6))
        .join(", ");
}

            // Format numeric values
            if (typeof value === "number") {
                return safeNumber(value).toFixed(6);
            }

            // Handle null values
            if (value === null || value === undefined) {
                return "";
            }

            return String(value);
        };

        // API property -> Custom UI name
        const fieldMap = {
            fB0: "B0",
            fB1: "B1",
            fB2: "B2",
            fB3: "B3",
            fB4: "B4",
            fGoodnessOfFit: "R2",
            fCalInterceptA0: "A0",
            fCalSlopeA1: "A1",
            fCalCurvatureA2: "A2",
            fCalBlankCorrectionFactorA3: "A3",
            fReportableRangeMin: "Cmin",
            fReportableRangeMax: "Cmax",
            fSplineX: "X",
            fSplineY: "Y",
            fSplineY2: "Y2",
            fKExponent: "K"
        };

        const result = {};

        // Return all API response properties using custom names
        Object.entries(data).forEach(([key, value]) => {
            const displayName = fieldMap[key] || key;

            result[displayName] = formatValue(value);
        });

        return result;
    };
    const validateCalibrationRows = (rows) => {
        if (!rows || rows.length === 0) return false;

        return rows.every(
            (r) =>
                r.conc !== "" &&
                r.conc !== null &&
                r.conc !== undefined &&
                r.resp !== "" &&
                r.resp !== null &&
                r.resp !== undefined
        );
    };

    // Button Click → Call API
    const handleSubmit = async () => {
        console.log("PARAMETERS:", parameters);
        console.log("ROWS:", rows);
      
        const isValid = validateCalibrationRows(rows);

        if (!isValid) {
            setValidationMessage(
                "Please fill all Concentration and Response values before calibration."
            );
            setOpenValidationDialog(true);
            return;
        }

        if (onCalibrate) {
            onCalibrate(); // expands Results panel
        }
        try {
            const apiData = await callApi();


            console.log("API RESPONSE:", apiData);

            const formatted = transformApiToHistory(apiData);

            if (!formatted) return;

            // Add the selected Math Model to calibration response
            formatted.eWetCalModelType = Number(
                parameters?.TXBVersion ?? 0
            );

            setResultDataMap(prev => {
                const prevData = prev || {};
                const nextRun = Object.keys(prevData).length + 1;

                const finalDescription =
                    description && description.trim() !== ""
                        ? description
                        : `Run ${nextRun}`;
                const newEntry = {
                    run: nextRun,
                    description: finalDescription,
                    result: formatted,
                    rows: rows,
                    parameters: parameters,
                    splines: splines
                };

                const updated = {
                    ...prevData,
                    [nextRun]: newEntry
                };

                // Persist in session
                sessionStorage.setItem("calibrationHistory", JSON.stringify(updated));

                return updated;
            });
            if (onCalibrate) onCalibrate();

        } catch (err) {
            console.error("API ERROR:", err);
        }
    };
    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" }, 
                justifyContent: "flex-start",
                backgroundColor: "#fff",                
            }}
        >
            {/* LEFT SECTION */}
            <Box
                sx={{
                    width: { xs: "100%", md: "80%" },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2, 
                    p: 2,
                    borderRadius: 3,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    alignItems: { xs: "stretch", sm: "center" },
                }}
            >
                {/* Dropdown */}
                <TextField
                    select
                    label="Math Model"
                    value={parameters?.TXBVersion ?? "0"}
                    onChange={(e) =>
                        setParameters(prev => ({
                            ...prev,
                            TXBVersion: e.target.value
                        }))
                    }
                    sx={{
                        flex: 1,
                        minWidth: 140,
                    }}
                    size="medium"
                >
                    <MenuItem value="0">Linear</MenuItem>
                    <MenuItem value="1">Logit4</MenuItem>
                    <MenuItem value="2">Logit5</MenuItem>
                    <MenuItem value="3">Cubic Spline</MenuItem>
                    <MenuItem value="4">K-Model</MenuItem>
                </TextField>
            
                {/* Description */}
                <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{
                        flex: 3,
                        minWidth: 200,
                    }}
                    size="medium"
                />

                {/* Button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    startIcon={<ScienceIcon />}
                    disabled={loading}
                    sx={{
                        whiteSpace: "nowrap",
                        minWidth: 140,
                        minHeight: 56,
                        px: 3,
                        alignSelf: { xs: "stretch", sm: "auto" },
                    }}
                >
                    {loading ? "Calibrate..." : "Calibrate"}
                </Button>

                {/* Error */}
                {error && (
                    <Typography
                        color="error"
                        sx={{
                            width: "100%",
                            mt: { xs: 1, sm: 0 },
                        }}
                    >
                        {error}
                    </Typography>
                )}
            </Box>

            {/* RIGHT SPACER (hidden on mobile) */}
            <Box
                sx={{
                    width: { xs: "0%", md: "20%" },
                    display: { xs: "none", md: "block" },
                }}
            />

            {/* Dialog */}
            <ConfirmDialog
                open={openValidationDialog}
                title="Validation Error"
                message={validationMessage}
                onConfirm={() => setOpenValidationDialog(false)}
                onCancel={() => setOpenValidationDialog(false)}
            />
        </Box>

        
    );
}